/**
 * Tracks, as rows rather than as a constraint (084).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A TRACK WITHOUT A TERMINAL STAGE IS A TRAP, AND THIS FILE IS THE GUARD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A terminal stage is the ONLY permanent exit from the accountability views.
 * A track with none chases every contact on it for ever, at contact_health's
 * hard-coded 7-day floor, and nothing anywhere reports that as wrong -- it
 * looks exactly like a track whose contacts are all genuinely overdue.
 *
 * The database cannot express "must have at least one terminal stage": it is
 * a cross-row rule, and a trigger enforcing it would refuse the first insert
 * of every new track. So 084 leaves the rule here, and `planNewTrack()`
 * creates the gate in the same breath as the track. There is no code path
 * that makes a bare one.
 *
 * WHY A SEPARATE FILE FROM lifecycleStageRules.ts. That module is about one
 * row's edit; this one is about the set a row belongs to. They are different
 * rule families and the split keeps both readable.
 */

import type { DataProvider } from "ra-core";

import type { LifecycleStage, LifecycleTrackRow } from "./types";

/* ── which track a new contact starts on ───────────────────────────────── */

/**
 * The track the editor preselects, or null if there are none at all.
 *
 * ORDERED, NOT `.find(is_default)`. 084 guarantees at MOST one default, not
 * exactly one -- "exactly one" needs a cross-row trigger that would refuse the
 * first insert. So a table with none set still has to produce an answer, and
 * the lowest `sort` is the one a person would expect.
 *
 * IT ONLY PRESELECTS. It does not give an untriaged contact a track: the row
 * is written when somebody saves, so "nobody has looked at this yet" stays
 * visible in the data rather than being quietly filled in.
 */
export function defaultTrack(
  tracks: readonly LifecycleTrackRow[],
): LifecycleTrackRow | null {
  const ordered = [...tracks].sort(
    (a, b) => Number(b.is_default) - Number(a.is_default) || a.sort - b.sort,
  );
  return ordered[0] ?? null;
}

/* ── adding one ────────────────────────────────────────────────────────── */

export type TrackDraft = {
  /** What a person types. The key is derived from it. */
  label: string;
  /** Kept as a string: see the header of lifecycleStageRules.ts. */
  cadenceDays: string;
};

export type TrackCheck =
  | { ok: false; errors: string[] }
  | { ok: true; errors: []; plan: NewTrackPlan };

/**
 * The three rows a new track is made of.
 *
 * ONE ACTIVE STAGE AND ONE GATE, ALWAYS. See the header: a track with no gate
 * is a trap, and a track with no active stage has nowhere to put anybody.
 * The names are deliberately generic -- somebody renaming "new" is a rename,
 * somebody discovering their track has no exit six weeks later is not.
 */
export type NewTrackPlan = {
  track: { track: string; label: string; sort: number; is_default: boolean };
  stages: Array<{
    track: string;
    stage: string;
    sort: number;
    stale_after_days: number | null;
    is_terminal: boolean;
  }>;
};

/**
 * A stable key from a label: lowercase, letters and digits, single hyphens.
 *
 * The key is the primary key and three foreign keys point at it, so it is
 * derived once and never regenerated from a later rename -- which is exactly
 * why `label` exists as its own column.
 */
export function trackKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The gate every new track gets. Terminal, so it carries no cadence. */
export const STARTER_GATE = "closed";
/** Where the first stage goes. */
export const STARTER_STAGE = "new";
/**
 * Gates sort after everything. Nothing enforces this -- the screen orders by
 * (is_terminal, sort) regardless -- but starting them high keeps a hand-run
 * `select … order by sort` reading the way the screen does.
 */
export const GATE_SORT_BASE = 90;

export function checkTrackDraft(
  draft: TrackDraft,
  existing: readonly LifecycleTrackRow[],
): TrackCheck {
  const errors: string[] = [];
  const label = draft.label.trim();
  const key = trackKey(label);

  if (label === "") errors.push("A track needs a name.");
  else if (key === "")
    errors.push(
      `“${label}” has no letters or digits in it, so it cannot become a key. ` +
        `The name is stored separately and can be anything; the key cannot.`,
    );
  else if (existing.some((t) => t.track === key))
    errors.push(
      `There is already a track keyed “${key}”. Two tracks with one key ` +
        `cannot both be referenced by contact_lifecycle.`,
    );

  const raw = draft.cadenceDays.trim();
  // Number("") is 0, which is a legal write and the worst one available: it
  // makes everybody on the track permanently overdue. Match digits first.
  const days = /^\d+$/.test(raw) ? Number(raw) : null;
  if (raw === "")
    errors.push(
      "Give the first stage a cadence — how often to chase somebody.",
    );
  else if (days === null || days < 1)
    errors.push(
      `“${raw}” is not a number of days. Zero is not a cadence either: it ` +
        `would make everybody on this track overdue for ever.`,
    );
  else if (days > 32767) errors.push("That is larger than the column holds.");

  if (errors.length > 0) return { ok: false, errors };

  const nextSort = existing.reduce((max, t) => Math.max(max, t.sort), 0) + 1;

  return {
    ok: true,
    errors: [],
    plan: {
      track: { track: key, label, sort: nextSort, is_default: false },
      stages: [
        {
          track: key,
          stage: STARTER_STAGE,
          sort: 1,
          stale_after_days: days,
          is_terminal: false,
        },
        {
          track: key,
          stage: STARTER_GATE,
          sort: GATE_SORT_BASE,
          stale_after_days: null,
          is_terminal: true,
        },
      ],
    },
  };
}

/* ── removing one ──────────────────────────────────────────────────────── */

export type RemovalVerdict = { ok: true } | { ok: false; reason: string };

/**
 * May this track be deleted?
 *
 * THE DATABASE REFUSES IT ANYWAY -- three foreign keys with no ON DELETE
 * clause. This exists so the screen can say WHY before somebody presses a
 * button and reads a constraint name, and so the count is in the sentence.
 */
export function trackRemovalVerdict(
  track: LifecycleTrackRow,
  stages: readonly LifecycleStage[],
  contactsOnTrack: number,
  allTracks: readonly LifecycleTrackRow[],
): RemovalVerdict {
  if (allTracks.length <= 1)
    return {
      ok: false,
      reason:
        "This is the only track. Removing it would leave no stage for any " +
        "contact to be on, and contact_lifecycle references them.",
    };

  if (contactsOnTrack > 0)
    return {
      ok: false,
      reason:
        `${contactsOnTrack} ${contactsOnTrack === 1 ? "contact is" : "contacts are"} ` +
        `on this track. Move them first — the database refuses the delete ` +
        `rather than cascading, because cascading would erase their stages.`,
    };

  const onTrack = stages.filter((s) => s.track === track.track);
  if (onTrack.length > 0)
    return {
      ok: false,
      reason:
        `${onTrack.length} ${onTrack.length === 1 ? "stage" : "stages"} still ` +
        `belong to this track. Remove them first.`,
    };

  return { ok: true };
}

/* ── the writes ────────────────────────────────────────────────────────── */

/**
 * Create a track and its starter stages.
 *
 * THE TRACK ROW GOES FIRST, and that ordering is not cosmetic: the stages
 * carry a foreign key to it, so the reverse order fails on the first stage
 * and leaves nothing behind. The failure mode is the good one -- a track that
 * exists with no stages is the trap this file is about, and it cannot be
 * reached from here because a stage failure leaves the track visible and
 * empty on screen where somebody will see it.
 */
export async function createTrack(
  dataProvider: DataProvider,
  plan: NewTrackPlan,
): Promise<void> {
  await dataProvider.create("lifecycle_tracks", { data: plan.track });
  for (const stage of plan.stages)
    await dataProvider.create("lifecycle_stages", { data: stage });
}

/**
 * Make one track the default, and no other.
 *
 * CLEARS THE OLD ONE FIRST. 084's partial unique index allows at most one, so
 * setting the new one first would be refused by the database -- correctly, and
 * confusingly, because the person only pressed one button.
 */
export async function setDefaultTrack(
  dataProvider: DataProvider,
  tracks: readonly LifecycleTrackRow[],
  next: string,
): Promise<void> {
  for (const t of tracks)
    if (t.is_default && t.track !== next)
      await dataProvider.update("lifecycle_tracks", {
        id: t.track,
        data: { is_default: false },
        previousData: t,
      });

  const target = tracks.find((t) => t.track === next);
  if (!target) throw new Error(`no track keyed ${next}`);
  if (target.is_default) return;

  await dataProvider.update("lifecycle_tracks", {
    id: next,
    data: { is_default: true },
    previousData: target,
  });
}
